import React, { useEffect, useState, useMemo } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as api from './api';
import './EventManager.css';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const formatDateRange = (startDate, endDate) => {
    if (!startDate) return '—';
    const s = new Date(startDate + 'T00:00:00');
    const month = MONTHS[s.getMonth()];
    const dayStart = s.getDate();
    if (!endDate || endDate === startDate) return `${month} ${dayStart}`;
    const e = new Date(endDate + 'T00:00:00');
    if (s.getMonth() === e.getMonth()) return `${month} ${dayStart}–${e.getDate()}`;
    return `${month} ${dayStart} – ${MONTHS[e.getMonth()]} ${e.getDate()}`;
};

const calcEventDays = (ev) => {
    if (!ev.startDate || !ev.endDate) return '?';
    const start = new Date(ev.startDate + 'T00:00:00');
    const end = new Date(ev.endDate + 'T00:00:00');
    return Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
};

const isPastEvent = (ev) => {
    const d = ev.endDate || ev.startDate;
    if (!d) return false;
    return new Date(d + 'T23:59:59') < new Date();
};

const getYear = (ev) => {
    const d = ev.startDate || ev.endDate;
    if (!d) return '????';
    return new Date(d + 'T00:00:00').getFullYear();
};

const EventBrowser = ({ token }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEventId, setSelectedEventId] = useState(null);

    api.setToken(token);

    useEffect(() => {
        const loadEvents = async () => {
            try {
                setLoading(true);
                const data = await api.fetchEvents();
                setEvents(data);
            } catch {
                toast.error('Failed to load events');
            } finally {
                setLoading(false);
            }
        };
        loadEvents();
    }, [token]);

    const eventsByYear = useMemo(() => {
        const groups = {};
        for (const ev of events) {
            const y = getYear(ev);
            if (!groups[y]) groups[y] = [];
            groups[y].push(ev);
        }
        return Object.keys(groups)
            .sort((a, b) => Number(b) - Number(a))
            .map(year => ({
                year,
                events: groups[year].sort((a, b) => {
                    const da = a.startDate || a.endDate || '';
                    const db = b.startDate || b.endDate || '';
                    return da.localeCompare(db);
                })
            }));
    }, [events]);

    const handleRegister = (ev) => {
        // Stub — functionality to be added later
    };

    return (
        <div className="em-container eb-narrow">
            <div className="em-header">
                <h1 className="em-title">Events</h1>
            </div>

            {loading ? (
                <div className="em-loading">Loading events…</div>
            ) : events.length === 0 ? (
                <div className="em-empty">No events scheduled.</div>
            ) : (
                eventsByYear.map(({ year, events: yearEvents }) => (
                    <div key={year} className="em-year-group">
                        <div className="em-year-header">{year}</div>
                        <table className="em-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Days</th>
                                    <th>Site</th>
                                    <th>Type</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {yearEvents.map(ev => {
                                    const past = isPastEvent(ev);
                                    const selected = selectedEventId === ev.eventId;
                                    return (
                                        <React.Fragment key={ev.eventId}>
                                            <tr
                                                className={`em-event-row ${past ? 'em-past' : 'em-future'} ${selected ? 'em-selected' : ''}`}
                                                onClick={() => setSelectedEventId(prev => prev === ev.eventId ? null : ev.eventId)}
                                            >
                                                <td className="em-date-cell">{formatDateRange(ev.startDate, ev.endDate)}</td>
                                                <td className="em-center">{calcEventDays(ev)}</td>
                                                <td>{ev.site}</td>
                                                <td>{ev.eventType}</td>
                                                <td className="em-desc-cell">{ev.description || '—'}</td>
                                            </tr>
                                            {selected && (
                                                <tr className="em-action-row">
                                                    <td colSpan={5}>
                                                        <div className="em-action-bar">
                                                            <button
                                                                className="em-btn em-btn-small em-btn-accent"
                                                                onClick={e => { e.stopPropagation(); handleRegister(ev); }}
                                                            >
                                                                Register For Event
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ))
            )}

            <ToastContainer position="bottom-right" autoClose={3000} />
        </div>
    );
};

export default EventBrowser;
